import { getCurrentInstance, toRaw, type InjectionKey } from "vue";
import {
  Store,
  useStore as baseUseStore,
  createLogger,
  createStore,
} from "vuex";

import { DefaultApi as Api, Configuration } from "@/api";

import {
  module as activityLog,
  type State as ActivityLogState,
} from "@/stores/activity_log";
import {
  module as automatedInvite,
  type State as AutomatedInviteState,
} from "@/stores/automated_invite";
import {
  module as automatedInviteDefault,
  type State as AutomatedInviteDefaultState,
} from "@/stores/automated_invite_default";
import {
  module as automatedInviteExcludedStage,
  type State as AutomatedInviteExcludedStageState,
} from "@/stores/automated_invite_excluded_stage";
import { module as call, type State as CallState } from "@/stores/call";
import {
  module as candidate,
  type State as CandidateState,
} from "@/stores/candidate";
import {
  module as candidateSource,
  type State as CandidateSourceState,
} from "@/stores/candidate_source";
import {
  module as company,
  type State as CompanyState,
} from "@/stores/company";
import {
  module as companyAnalysis,
  type State as CompanyAnalysisState,
} from "@/stores/company_analysis";
import {
  module as dashboard,
  type State as DashboardState,
} from "@/stores/dashboard";
import {
  module as evaluationEmailRecipient,
  type State as EvaluationEmailRecipientState,
} from "@/stores/evaluation_email_recipient";
import {
  module as evaluationEmailSetting,
  type State as EvaluationEmailSettingState,
} from "@/stores/evaluation_email_setting";
import {
  module as evaluation,
  type State as EvaluationState,
} from "@/stores/evaluation";
import {
  module as evaluationChats,
  type State as EvaluationChatsState,
} from "@/stores/evaluation_chat";
import {
  module as helpCenterChat,
  type State as HelpCenterChatState,
} from "@/stores/help_center_chat";
import {
  module as jobAnalysisTracker,
  type State as JobAnalysisTrackerState,
} from "@/stores/job_analysis_tracker";
import {
  module as integration,
  type State as IntegrationState,
} from "@/stores/integration";
import {
  module as inviteUser,
  type State as InviteUserState,
} from "@/stores/invite_user";
import {
  module as jobAnalysis,
  type State as JobAnalysisState,
} from "@/stores/job_analysis";
import {
  module as jobBoardBoost,
  type State as JobBoardBoostState,
} from "@/stores/job_board_boost";
import {
  module as jobState,
  type State as JobStateState,
} from "@/stores/job_state";
import {
  module as organization,
  type State as OrganizationState,
} from "@/stores/organization";
import {
  module as position,
  type State as PositionState,
} from "@/stores/position";
import {
  module as positionChat,
  type State as PositionChatState,
} from "@/stores/position_chat";
import {
  module as positionQualificationChat,
  type State as PositionQualificationChatState,
} from "@/stores/position_qualification_chat";
import {
  module as rejection,
  type State as RejectionState,
} from "@/stores/rejection";
import { module as resume, type State as ResumeState } from "@/stores/resume";
import {
  module as resumeChat,
  type State as ResumeChatState,
} from "@/stores/resume_chat";
import {
  module as shortMessage,
  type State as ShortMessageState,
} from "@/stores/short_message";
import {
  module as sidebar,
  type State as SidebarState,
} from "@/stores/sidebar";
import { module as stages, type State as StagesState } from "@/stores/stage";
import {
  module as stageTransitions,
  type State as StageTransitionsState,
} from "@/stores/stage_transition";
import {
  module as stageTransitionsDefault,
  type State as StageTransitionsDefaultState,
} from "@/stores/stage_transitions_default";
import {
  module as statistics,
  type State as StatisticsState,
} from "@/stores/statistics";
import {
  module as subscription,
  type State as SubscriptionState,
} from "@/stores/subscription";
import {
  module as summaryRecipient,
  type State as SummaryRecipientState,
} from "@/stores/summary_recipient";
import {
  module as todoGuidance,
  type State as TodoGuidanceState,
} from "@/stores/todo_guidance";
import {
  module as unresponsiveCandidateLog,
  type State as UnresponsiveCandidateLogState,
} from "@/stores/unresponsive_candidate_log";
import {
  module as onboardingTour,
  type State as OnboardingTourState,
} from "@/stores/onboarding_tour";
import {
  module as roiMetrics,
  type State as RoiMetricsState,
} from "@/stores/roi_metrics";
import {
  module as reviewItems,
  type State as ReviewItemsState,
} from "@/stores/review_items";
import {
  module as profile,
  type State as ProfileState,
} from "@/stores/profile";

const debug = import.meta.env.DEV;

export interface State {
  apiBase: string | undefined;
  activityLog?: ActivityLogState;
  call?: CallState;
  candidate?: CandidateState;
  candidateSource?: CandidateSourceState;
  company?: CompanyState;
  companyAnalysis?: CompanyAnalysisState;
  dashboard?: DashboardState;
  evaluation?: EvaluationState;
  integration?: IntegrationState;
  jobAnalysis?: JobAnalysisState;
  jobBoardBoost?: JobBoardBoostState;
  jobState?: JobStateState;
  position?: PositionState;
  positionChat?: PositionChatState;
  positionQualificationChat?: PositionQualificationChatState;
  helpCenterChat?: HelpCenterChatState;
  jobAnalysisTracker?: JobAnalysisTrackerState;
  resume?: ResumeState;
  resumeChat?: ResumeChatState;
  shortMessage?: ShortMessageState;
  statistics?: StatisticsState;
  subscription?: SubscriptionState;
  stages?: StagesState;
  stageTransitions?: StageTransitionsState;
  evaluationChats?: EvaluationChatsState;
  rejection?: RejectionState;
  automatedInvite?: AutomatedInviteState;
  automatedInviteDefault?: AutomatedInviteDefaultState;
  automatedInviteExcludedStage?: AutomatedInviteExcludedStageState;
  summaryRecipient?: SummaryRecipientState;
  stageTransitionsDefault?: StageTransitionsDefaultState;
  organization?: OrganizationState;
  todoGuidance?: TodoGuidanceState;
  inviteUser?: InviteUserState;
  evaluationEmailRecipient?: EvaluationEmailRecipientState;
  evaluationEmailSetting?: EvaluationEmailSettingState;
  sidebar?: SidebarState;
  unresponsiveCandidateLog?: UnresponsiveCandidateLogState;
  roiMetrics?: RoiMetricsState;
  reviewItems?: ReviewItemsState;
  onboardingTour?: OnboardingTourState;
  profile?: ProfileState;
}

import { authenticatedApi, useAuth } from "@/utils/authentication";
import { guardTenantModule } from "@/stores/tenant_lifecycle";

export const key: InjectionKey<Store<State>> = Symbol();

export const store = createStore<State>({
  state: {
    apiBase: undefined,
  },
  modules: Object.fromEntries(
    Object.entries({
      activityLog,
      call,
      candidate,
      candidateSource,
      company,
      companyAnalysis,
      dashboard,
      evaluation,
      integration,
      jobAnalysis,
      jobBoardBoost,
      jobState,
      position,
      positionChat,
      positionQualificationChat,
      helpCenterChat,
      jobAnalysisTracker,
      resume,
      resumeChat,
      shortMessage,
      statistics,
      subscription,
      stages,
      stageTransitions,
      evaluationChats,
      rejection,
      automatedInvite,
      automatedInviteDefault,
      automatedInviteExcludedStage,
      summaryRecipient,
      stageTransitionsDefault,
      organization,
      todoGuidance,
      inviteUser,
      evaluationEmailRecipient,
      evaluationEmailSetting,
      sidebar,
      unresponsiveCandidateLog,
      roiMetrics,
      reviewItems,
      onboardingTour,
      profile,
    }).map(([name, module]) => [name, guardTenantModule<any, State>(module)]),
  ),
  actions: {
    getAuthorizedApi({ state }) {
      if (!useAuth().isAuthenticated.value)
        throw new Error("Authentication is required to access the API");
      return new Api(
        new Configuration({
          basePath: state.apiBase,
        }),
        state.apiBase,
        authenticatedApi,
      );
    },
  },
  mutations: {
    setApiBase(state, apiBase: string) {
      state.apiBase = apiBase;
    },
  },
  strict: debug,
  plugins: debug ? [createLogger()] : [],
});

const initialTenantState = structuredClone(toRaw(store.state));

export const resetTenantState = () => {
  store.replaceState({
    ...structuredClone(initialTenantState),
    apiBase: store.state.apiBase,
  });
};

export function useStore() {
  if (store.state.apiBase === undefined) {
    store.commit(
      "setApiBase",
      getCurrentInstance()?.appContext.app.config.globalProperties.$apiBase,
    );
  }
  return baseUseStore(key);
}
